'use client';

import React, { useMemo } from 'react';
import { useGetSystemSitesQuery } from 'src/api/redux';
import WidgetLoading from 'src/components/Profiles/shared/widgets/shared/WidgetLoading';
import WidgetEmpty from 'src/components/Profiles/shared/widgets/shared/WidgetEmpty';
import { useProfile } from 'src/context/providers/ProfileProvider';
import { useWidgetConfig } from 'src/context/providers/WidgetConfigProvider';
import { getEntityIdentifiers } from 'src/utils/getEntityIdentifiers';

const PAGE_SIZE = 3;

const CompactSitesBySpecialtyPreview: React.FC = () => {
  const { entityDetails } = useProfile();
  const widgetConfig = useWidgetConfig();

  const { providerId: systemId } = getEntityIdentifiers(entityDetails);

  const volumeFilterSetId = widgetConfig?.filterSet?.id;
  const volumeKey = volumeFilterSetId ? String(volumeFilterSetId) : null;
  const widgetFilterSetId = widgetConfig?.filterSetId;

  const queryArgs = useMemo(() => {
    const args: Record<string, any> = {
      systemId,
      page: 1,
      pageSize: PAGE_SIZE,
    };

    if (volumeKey) {
      args.sort = 'volume';
      args.sort_id = volumeKey;
      args.sort_field = 'patients';
      args.order = 'desc';
    } else {
      args.sort = 'name';
      args.order = 'asc';
    }

    if (widgetFilterSetId) {
      args.filterSets = [widgetFilterSetId];
    }

    if (volumeKey) {
      args.data = {
        [volumeKey]: {
          type: 'patients',
          date_range: {
            gte: widgetConfig?.minYear,
            lte: widgetConfig?.maxYear,
          },
          filter_sets: [],
          filters: [],
        },
      };
    } else {
      args.data = {};
    }

    return args;
  }, [
    systemId,
    volumeKey,
    widgetFilterSetId,
    widgetConfig?.minYear,
    widgetConfig?.maxYear,
  ]);

  const { data, isLoading } = useGetSystemSitesQuery(queryArgs, {
    skip: !systemId,
  });

  const rows = data?.results || [];

  if (isLoading || widgetConfig?.isLoading) {
    return <WidgetLoading data-testid="compact-sites-by-specialty-preview-loading" aria-label="Loading sites" />;
  }

  if (rows.length === 0) {
    return <WidgetEmpty data-testid="compact-sites-by-specialty-preview-empty" />;
  }

  return (
    <div
      className="flex flex-col"
      data-testid="compact-sites-by-specialty-preview"
      role="list"
      aria-label="Sites"
    >
      {rows.map((site: Record<string, any>) => (
        <div
          key={site.provider_id}
          className="flex items-center justify-between gap-2 py-1.5 border-b border-neutral-100 last:border-b-0"
          role="listitem"
        >
          <span className="text-sm font-medium text-primary truncate">
            {site.name || 'N/A'}
          </span>
          <div className="flex items-center gap-2 flex-shrink-0">
            <span className="text-xs font-semibold text-teal-900 tabular-nums">
              {volumeKey && site.patients?.[volumeKey] != null
                ? `${Number(site.patients[volumeKey]).toLocaleString()} pts`
                : '-'}
            </span>
            <span className="text-xs font-semibold text-teal-900 tabular-nums">
              {volumeKey && site.claims?.[volumeKey] != null
                ? `${Number(site.claims[volumeKey]).toLocaleString()} claims`
                : '-'}
            </span>
          </div>
        </div>
      ))}
    </div>
  );
};

export default CompactSitesBySpecialtyPreview;
